import { Injectable } from '@angular/core';
import { Apollo, QueryRef } from 'apollo-angular';
import ID from '../../../types/id';
import {
  CREATE_CHECKLIST_TASK,
  CreateChecklistTaskInput,
  DELETE_CHECKLIST_TASK,
  FETCH_REQUEST_WITH_CHECKLIST,
  REFRESH_CHECKLIST_TASKS,
  RefreshChecklistTasksResponse,
  RefreshChecklistTasksVariables,
  UPDATE_CHECKLIST_TASK,
  UpdateChecklistTaskInput,
} from './graphql/individual-checklist';
import { ConsentRequest } from '../../../interfaces/consent-request';
import { BehaviorSubject, Subscription } from 'rxjs';
import ConsentChecklistTask from '../../../interfaces/consent-checklist-task';
import ConsentChecklistStatus from '../../../interfaces/consent-checklist-status';
import { ConsentRequestRoleGroup } from '../../../interfaces/consent-request-role-group';
import { DataFormatService } from '../../../services/data-format.service';
import { Person } from '../../../interfaces/person';
import Permission from '../../../interfaces/permission';
import Group from '../../../interfaces/group';

@Injectable({
  providedIn: 'root',
})
export class IndividualChecklistService {
  private _consentRequest: ConsentRequest | null = null;
  private consentRequest = new BehaviorSubject(this._consentRequest);
  public consentRequest$ = this.consentRequest.asObservable();

  private _consentChecklistTasks: ConsentChecklistTask[] = [];
  private consentChecklistTasks = new BehaviorSubject(
    this._consentChecklistTasks
  );
  public consentChecklistTasks$ = this.consentChecklistTasks.asObservable();

  private _consentChecklistStatuses: ConsentChecklistStatus[] = [];
  private consentChecklistStatuses = new BehaviorSubject(
    this._consentChecklistStatuses
  );
  public consentChecklistStatuses$ =
    this.consentChecklistStatuses.asObservable();

  private _roleGroups: ConsentRequestRoleGroup[] = [];
  private roleGroups = new BehaviorSubject(this._roleGroups);
  public roleGroups$ = this.roleGroups.asObservable();

  private _groups: Group[] = [];
  private groups = new BehaviorSubject(this._groups);
  public groups$ = this.groups.asObservable();

  private _loading: boolean = false;
  private loading = new BehaviorSubject(this._loading);
  public loading$ = this.loading.asObservable();

  private _tasksLoading: boolean = false;
  private tasksLoading = new BehaviorSubject(this._tasksLoading);
  public tasksLoading$ = this.tasksLoading.asObservable();

  private _userGroups: Group[] = [];
  private userGroups = new BehaviorSubject(this._userGroups);
  public userGroups$ = this.userGroups.asObservable();

  private _permission: Permission = {
    _create: false,
    _read: false,
    _edit: false,
    _delete: false,
    authorized_fields: null,
    __typename: 'Permission',
  };
  private permission = new BehaviorSubject(this._permission);
  public permission$ = this.permission.asObservable();

  private refreshQuery: QueryRef<
    RefreshChecklistTasksResponse,
    RefreshChecklistTasksVariables
  >;
  private refreshSub: Subscription;
  constructor(private apollo: Apollo, private formatter: DataFormatService) {}

  private setPermission(permission?: Permission) {
    if (permission) {
      this._permission = permission;
      this.permission.next(this._permission);
    }
  }
  private setLoading(loading: boolean) {
    this._loading = loading;
    this.loading.next(this._loading);
    this.setTasksLoading(loading);
  }
  private setTasksLoading(loading: boolean) {
    this._tasksLoading = loading;
    this.tasksLoading.next(this._loading);
  }
  private setConsentRequest(consentRequest: ConsentRequest) {
    this._consentRequest = consentRequest;
    this.consentRequest.next(this._consentRequest);
  }
  private setRoleGroups(roleGroups: ConsentRequestRoleGroup[]) {
    this._roleGroups = roleGroups;
    this.roleGroups.next(this._roleGroups);
  }
  private setUserGroups(groups: Group[]) {
    this._userGroups = groups;
    this.userGroups.next(this._userGroups);
  }
  private setConsentChecklistTasks(
    consentChecklistTasks: ConsentChecklistTask[]
  ) {
    this._consentChecklistTasks = consentChecklistTasks;
    this.consentChecklistTasks.next(this._consentChecklistTasks);
  }
  private setGroups(roleGroups: ConsentRequestRoleGroup[]) {
    this._groups = roleGroups
      .slice()
      .map((roleGroup) => roleGroup.group as Group);
    this.groups.next(this._groups);
  }
  public getStatus(checklist_status_id: ID): ConsentChecklistStatus | null {
    return (
      this._consentChecklistStatuses.find(
        (status) => status.id === checklist_status_id
      ) ?? null
    );
  }
  private setChecklistStatuses(checklistStatuses: ConsentChecklistStatus[]) {
    this._consentChecklistStatuses = checklistStatuses;
    this.consentChecklistStatuses.next(this._consentChecklistStatuses);
  }

  public getConsentChecklist(id: ID) {
    this.setLoading(true);
    this.apollo
      .query({
        query: FETCH_REQUEST_WITH_CHECKLIST,
        variables: { id },
        fetchPolicy: 'network-only',
      })
      .subscribe(({ data }) => {
        this.setLoading(false);
        if (!data) return;
        let consentChecklistTasks =
          data.getConsentRequest.consentChecklistTasks ?? [];
        this.setRoleGroups(data.getConsentRequestRoleGroups);
        this.setConsentRequest(data.getConsentRequest);
        this.setConsentChecklistTasks(consentChecklistTasks);
        this.setGroups(data.getConsentRequestRoleGroups);
        this.setChecklistStatuses(data.getConsentChecklistStatuses);
        this.setPermission(data.me.permissionsForResource);
        if (data.me.groupsFlattened) {
          this.setUserGroups(data.me.groupsFlattened);
        }
      });
  }
  public inGroup(id: ID): boolean {
    if (
      !!this._permission._edit &&
      this._permission.authorized_fields === null
    ) {
      return true;
    }
    let group = this._userGroups.find((userGroup) => id === userGroup.id);
    return !!group;
  }
  public refreshConsentChecklist(consent_request_id: ID) {
    this.setTasksLoading(true);
    if (!this.refreshQuery) {
      this.refreshQuery = this.apollo.watchQuery({
        query: REFRESH_CHECKLIST_TASKS,
        variables: { consent_request_id },
      });
      if (!this.refreshSub) {
        this.refreshSub = this.refreshQuery.valueChanges.subscribe(
          ({ data }) => {
            this.setTasksLoading(false);
            if (data) {
              this.setConsentChecklistTasks(
                data.refreshChecklistTasks as ConsentChecklistTask[]
              );
            }
          }
        );
      }
    } else {
      this.refreshQuery.refetch();
    }
  }
  public getResponsibleParty(task: ConsentChecklistTask): Person | string {
    if (task.group) {
      let mapper = this._roleGroups.find(
        (roleGroup) => roleGroup.group_id === task.group?.id
      );
      if (mapper) {
        let role = this.formatter.snakeCaseToCamelCase(
          mapper.role_column_name.replace('_id', '')
        );
        if (
          this._consentRequest &&
          this._consentRequest.hasOwnProperty(role) &&
          this._consentRequest[role as unknown as keyof ConsentRequest]
        ) {
          return this._consentRequest[role as keyof ConsentRequest] as Person;
        }
      }
      return task.group.name;
    }

    return '\u2014';
  }
  public createTask(input: CreateChecklistTaskInput) {
    this.setTasksLoading(true);
    return this.apollo.mutate({
      mutation: CREATE_CHECKLIST_TASK,
      variables: { input },
    });
  }
  public updateTask(id: ID, input: UpdateChecklistTaskInput) {
    this.setTasksLoading(true);
    return this.apollo.mutate({
      mutation: UPDATE_CHECKLIST_TASK,
      variables: {
        id,
        input,
      },
    });
  }
  public deleteTask(id: ID) {
    this.setTasksLoading(true);
    return this.apollo.mutate({
      mutation: DELETE_CHECKLIST_TASK,
      variables: {
        id,
      },
    });
  }
  public tearDown() {
    this.refreshSub?.unsubscribe();
  }
}
